<template>
  <div class="b-booking-aside">
    <div class="b-booking-aside__top-section">
      <div class="b-booking-aside__header">
        <span class="b-booking-aside__title h2">
          {{ dictionary.asideHeaderTitle }}
        </span>

        <button
          type="button"
          class="btn btn-link-light btn-icon-start btn-edit"
          @click="onClickChangeVehicle">
          <i class="icon-edit"></i>
          <small>{{ dictionary.buttonChangeVehicleText }}</small>
        </button>
      </div>
      <div class="b-booking-aside__vehicle">
        <div class="b-vehicle view-list">
          <div class="b-vehicle__body">
            <div class="b-vehicle__header mb-40">
              <div class="b-vehicle__title h4 extrabold">
                {{ carObj?.manufacturer }} {{ carObj?.short_model_name }}
              </div>
              <div class="b-vehicle__groups mb-10 semibold">
                <ul>
                  <li>{{ modelNameComputed }}</li>
                </ul>
              </div>
              <div class="b-vehicle__details">
                <ul>
                  <li>
                    <div class="pair bold">
                      <i class="icon-transmission"></i>
                      <span>
                        {{ transmissionLabel }}
                      </span>
                    </div>
                  </li>
                  <li>
                    <div class="pair bold">
                      <i class="icon-fuel"></i>
                      {{ carObj?.fuel_type }}
                    </div>
                  </li>
                  <li>
                    <div class="pair bold">
                      <i class="icon-emmission"></i>
                      {{ carObj?.co2 }} gr/km
                    </div>
                  </li>
                  <li>
                    <div class="pair bold">
                      <i class="icon-consumption"></i>
                      {{ carObj?.consumption }}/100km
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            <SlickCarousel
              v-if="images.length"
              ref="slick"
              :key="'key-' + slickKey"
              class="b-vehicle__slick mb-30"
              :options="slickOptions">
              <div v-for="(image, index) in images" :key="index" class="item">
                <LazyImage v-if="image" :image="image" />
              </div>
            </SlickCarousel>

            <hr class="separator mb-12" />

            <div class="b-vehicle__payments columns-2 mb-12">
              <ul>
                <li>
                  <span class="label">
                    {{ dictionary.retailPriceBeforeTaxesLabel }}
                  </span>
                  <em>€{{ priceExclTaxFormatted }}</em>
                </li>
                <li>
                  <span class="label">
                    {{ dictionary.retailPriceLabel }}
                  </span>
                  <em>€{{ priceFormatted }}</em>
                </li>
                <li v-if="extraPrice > 0">
                  <span class="label">{{ dictionary.extrasPriceLabel }}</span>
                  <em>€{{ extraPriceFormatted }}</em>
                </li>
                <li v-if="extraPrice > 0">
                  <span class="label">{{ dictionary.finalPriceLabel }}</span>
                  <em>€{{ finalPriceFormatted }}</em>
                </li>
                <li v-if="buyback">
                  <span class="label">{{ dictionary.buybackAmountLabel }}</span>
                  <em v-if="vatIncluded">
                    €{{ buybackAmountVatIncluded?.toLocaleString(lang) }}
                  </em>
                  <em v-else>€{{ buybackAmount?.toLocaleString(lang) }}</em>
                </li>
              </ul>
            </div>

            <hr class="separator mb-24" />

            <div
              class="b-vehicle__rent-footer b-vehicle__rent-footer--wrapped mb-30">
              <div class="bold">{{ dictionary.monthlyRentalLabel }}</div>

              <div class="b-vehicle__totals mb-20">
                <div
                  class="b-vehicle__price-w-vat"
                  :class="{ 'is-active': vatIncluded }">
                  <div class="bold mb-5">
                    <span class="animate">
                      <em v-if="vatIncluded">
                        €{{ rentalPriceVatIncludedFormatted }}
                      </em>
                      <em v-else>€{{ rentalPriceFormatted }}</em>
                      {{ dictionary.vehiclePriceSuffix }}
                    </span>
                  </div>

                  <div class="form-check form-switch">
                    <label class="form-check-label" for="checkboxVat">
                      <span v-if="vatIncluded">
                        {{ dictionary.vehicleVatIncluded }}
                      </span>
                      <span v-else>{{ dictionary.vehicleVatExcluded }}</span>
                    </label>
                    <input
                      id="checkboxVat"
                      v-model="vatIncluded"
                      class="form-check-input"
                      type="checkbox"
                      role="switch" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as lib from './../../../lib/lib'
import constants from './../../../lib/constants'
import { v4 as uuidv4 } from 'uuid'
import LazyImage from './../common/LazyImage.vue'
import SlickCarousel from './../common/SlickCarousel.vue'
import mixinsLeaseItNow from './../../mixins/leaseitnow-mixins'

export default {
  components: { LazyImage, SlickCarousel },
  mixins: [mixinsLeaseItNow],
  props: {
    carObj: {
      required: true,
      type: Object,
    },
  },
  data() {
    return {
      lang: 'en',
      isLoading: false,
      errorMsgs: [],
      vatIncluded: false,
      slickKey: uuidv4(),
      slickOptions: {
        infinite: true,
        autoplay: false,
        arrows: true,
        dots: false,
        slidesToShow: 1,
        slidesToScroll: 1,
        centerMode: false,
        variableWidth: false,
      },
    }
  },
  computed: {
    modelNameComputed() {
      if (!this.carObj) {
        return null
      }
      /* eslint-disable camelcase */
      const { model_name, short_model_name } = this.carObj
      const modelName = model_name.replace(short_model_name, '')

      return `${modelName} ${this.carObj.model_key}`
    },

    priceExclTaxFormatted() {
      return this.carObj
        ? Math.round(this.carObj.price_excl_tax_num).toLocaleString(this.lang)
        : null
    },

    priceFormatted() {
      return this.carObj
        ? this.carObj.price_num.toLocaleString(this.lang)
        : null
    },

    rentalPriceFormatted() {
      return this.rentalPrice
        ? Math.round(this.rentalPrice).toLocaleString(this.lang)
        : null
    },

    rentalPriceVatIncludedFormatted() {
      if (!this.rentalPrice) {
        return null
      }
      if (!this.vat) {
        return null
      }
      return Math.round(this.rentalPrice + this.vat).toLocaleString(this.lang)
    },

    transmissionLabel() {
      if (!this.carObj) {
        return null
      }

      let res
      const transmission = this.carObj.transmission
      const { manual, auto } = constants.leaseitnow.filters.transmissionsOptions

      switch (transmission) {
        case manual:
          res = this.dictionary.transmissionManual
          break
        case auto:
          res = this.dictionary.transmissionAutomatic
          break
        default:
          res = null
      }
      return res
    },

    images() {
      if (!this.carObj) {
        return []
      }

      const images = this.carObj.slider_images
      if (!images.length) {
        return []
      }

      return images.map((x) => ({ url: x, alt: '' }))
    },

    extraPriceFormatted() {
      return Math.round(this.extraPrice).toLocaleString(this.lang)
    },

    finalPriceFormatted() {
      return Math.round(this.finalPrice).toLocaleString(this.lang)
    },

    buybackAmountVatIncluded() {
      const { vat } = this.configuration
      return this.buybackAmount + this.buybackAmount * (vat / 100)
    },
  },
  mounted() {
    this.lang = lib.getLocaleFromLangAttr()
    const { sessionStorageConfig } = constants.leaseitnow
    const carConfig = JSON.parse(sessionStorage.getItem(sessionStorageConfig))

    if (carConfig) {
      this.vatIncluded = carConfig.vatIncluded
    }
  },
  methods: {
    onClickChangeVehicle() {
      const { language } = this.$route.params
      const { sessionStorageKey } = constants.leaseitnow
      const searchargs = sessionStorage.getItem(sessionStorageKey)
      this.$router.push({
        name: 'home',
        params: { language },
        query: { searchargs },
      })

      lib.scrollToTop()
    },
    onClickGoBack() {
      const { language } = this.$route.params
      const { sessionStorageKey } = constants.leaseitnow
      const searchargs = sessionStorage.getItem(sessionStorageKey)
      this.$router.push({
        name: 'car',
        params: { language },
        query: { searchargs, hasExtrasArgs: true, hasRentalArgs: true },
      })

      lib.scrollToTop()
    },
  },
}
</script>
